import React, { useEffect, useState } from "react"
import { useLazyQuery } from "@apollo/client"
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import esb from "elastic-builder"
import { Query } from "lib/types/common"
import SEARCH_ORDERS_TOTAL_HITS from "../../graphql/queries/order/SearchOrdersTotalHits"
import { theme } from "../../theme"
import {
  ChartContainer,
  EmptyState,
  Header,
  Title,
  ToggleButton,
  ToggleContainer,
  Wrapper
} from "./OrdersOverTimeChart.styled"

dayjs.extend(utc)
dayjs.extend(timezone)

type TimeInterval = "hourly" | "daily"

interface OrdersOverTimeChartProps {
  startDate: string
  endDate: string
  timeZone: string
  storeGroups: { value: string; label: string }[]
  storeMarkets: { value: string; label: string }[]
  campaignGroups: { value: string; label: string }[]
  campaigns: { value: string; label: string }[]
}

interface TimeSeriesData {
  timestamp: string
  orders: number
  displayLabel: string
}

export const OrdersOverTimeChart: React.FC<OrdersOverTimeChartProps> = ({
  startDate,
  endDate,
  timeZone,
  storeGroups,
  storeMarkets,
  campaignGroups,
  campaigns
}) => {
  const [interval, setInterval] = useState<TimeInterval>("hourly")
  const [chartData, setChartData] = useState<TimeSeriesData[]>([])
  const timeRangeDays = dayjs(endDate).diff(startDate, "day")

  const [searchOrders, { loading, error }] = useLazyQuery(SEARCH_ORDERS_TOTAL_HITS, {
    onCompleted: (data) => {
      if (data?.searchOrders?.aggregations) {
        const aggregations = JSON.parse(data.searchOrders.aggregations)
        const buckets = aggregations?.ordersOverTime?.buckets || []

        const formattedData: TimeSeriesData[] = buckets.map(
          (bucket: { key_as_string: string; doc_count: number }) => ({
            timestamp: bucket.key_as_string,
            orders: bucket.doc_count,
            displayLabel: formatLabel(bucket.key_as_string, interval)
          })
        )

        setChartData(formattedData)
      }
    }
  })

  const formatLabel = (timestamp: string, currentInterval: TimeInterval): string => {
    const date = dayjs(timestamp).tz(timeZone)
    if (currentInterval === "hourly") {
      return date.format("MMM D, HH:mm")
    }
    return date.format("MMM D")
  }

  useEffect(() => {
    const startDateCheck = new Date(startDate)
    const startDateIsValid = startDateCheck && !isNaN(startDateCheck.getTime())
    const endDateCheck = new Date(endDate)
    const endDateIsValid = endDateCheck && !isNaN(endDateCheck.getTime())

    if (timeRangeDays === 0) setInterval("hourly")

    if (!startDateIsValid || !endDateIsValid) return

    const boolQuery = esb
      .boolQuery()
      .must(
        esb
          .rangeQuery("date")
          .gt(dayjs(startDate).format("YYYY-MM-DD-HH:mm"))
          .lt(dayjs(endDate).format("YYYY-MM-DD-HH:mm"))
          .format("yyyy-MM-dd-HH:mm")
          .timeZone(timeZone)
      )

    if (storeGroups?.length > 0) {
      boolQuery.must(
        esb.termsQuery(
          "storeGroupId",
          storeGroups.map((option) => option.value)
        )
      )
    }

    if (storeMarkets?.length > 0) {
      boolQuery.must(
        esb.termsQuery(
          "countryCode",
          storeMarkets.map((option) => option.value)
        )
      )
    }

    if (campaigns.length > 0) {
      boolQuery.must(
        esb
          .nestedQuery()
          .path("orderLines")
          .query(
            esb.termsQuery(
              "orderLines.campaign.campaignId",
              campaigns.map((campaign) => campaign.value)
            )
          )
      )
    } else if (campaignGroups.length > 0) {
      boolQuery.must(
        esb
          .nestedQuery()
          .path("orderLines")
          .query(
            esb.termsQuery(
              "orderLines.campaign.campaignGroupId",
              campaignGroups.map((group) => group.value)
            )
          )
      )
    }

    const dateHistogramInterval = interval === "hourly" ? "1h" : "1d"

    const aggs = esb
      .requestBodySearch()
      .agg(
        esb
          .dateHistogramAggregation("ordersOverTime", "date")
          .interval(dateHistogramInterval)
          .timeZone(timeZone)
          .minDocCount(0)
          .extendedBounds(dayjs(startDate).valueOf(), dayjs(endDate).valueOf())
      )
      .toJSON() as Query

    const dailyQuery = esb.requestBodySearch().query(boolQuery)
    const query = dailyQuery.toJSON() as Query

    const delayDebounceFn = setTimeout(() => {
      if (query) {
        searchOrders({
          variables: {
            from: 0,
            size: 0,
            query: JSON.stringify({
              bool: {
                must: [query.query]
              }
            }),
            trackTotalHits: false,
            aggs: JSON.stringify(aggs.aggs)
          }
        })
      }
    }, 300)

    return () => clearTimeout(delayDebounceFn)
  }, [startDate, endDate, timeZone, storeGroups, storeMarkets, campaignGroups, campaigns, interval])

  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean
    payload?: { value: number; payload: TimeSeriesData }[]
  }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            background: theme.colors.white,
            border: `1px solid ${theme.colors.greyLight}`,
            borderRadius: "0.8rem",
            padding: "1rem",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)"
          }}
        >
          <p style={{ margin: 0, fontSize: "1.2rem", fontWeight: 600 }}>
            {payload[0].payload.displayLabel}
          </p>
          <p
            style={{
              margin: "0.5rem 0 0 0",
              fontSize: "1.4rem",
              color: theme.colors.turquoiseDark
            }}
          >
            Orders: {payload[0].value}
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <Wrapper>
      <Header>
        <Title>Orders Over Time</Title>
        {timeRangeDays > 0 && (
          <ToggleContainer>
            <ToggleButton $active={interval === "hourly"} onClick={() => setInterval("hourly")}>
              Hourly
            </ToggleButton>
            <ToggleButton $active={interval === "daily"} onClick={() => setInterval("daily")}>
              Daily
            </ToggleButton>
          </ToggleContainer>
        )}
      </Header>

      {loading ? (
        <EmptyState>Loading...</EmptyState>
      ) : error ? (
        <EmptyState>Error loading chart data</EmptyState>
      ) : chartData.length === 0 ? (
        <EmptyState>No data available</EmptyState>
      ) : (
        <ChartContainer>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 0
              }}
            >
              <defs>
                <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.colors.turquoiseDark} stopOpacity={0.8} />
                  <stop offset="100%" stopColor={theme.colors.turquoiseDark} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="displayLabel"
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="orders"
                stroke={theme.colors.turquoiseDark}
                fillOpacity={1}
                fill="url(#colorOrders)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
      )}
    </Wrapper>
  )
}
